"use client";

import React, { useEffect, useRef } from "react";

type ScrollRevealProps = {
  children: React.ReactNode;
  /** Tailwind / utility class list applied to the wrapper. */
  className?: string;
  /** Animation variant. Defaults to `up`. */
  variant?: "up" | "fade" | "scale";
  /** Reveal delay in ms (0–800 recommended). */
  delay?: number;
  /** Intersection threshold (0–1). Defaults to 0.18. */
  threshold?: number;
  /** Render as a different element. Defaults to `div`. */
  as?: keyof React.JSX.IntrinsicElements;
  /** Reveal once and disconnect. Defaults to true. */
  once?: boolean;
};

/**
 * Reveals its children when scrolled into view.
 *
 * Honors `prefers-reduced-motion`: reduced-motion users see content immediately
 * with no transform/opacity transition.
 */
export function ScrollReveal({
  children,
  className = "",
  variant = "up",
  delay = 0,
  threshold = 0.18,
  as = "div",
  once = true,
}: ScrollRevealProps) {
  const ref = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      el.classList.add("is-visible");
      return;
    }

    if (delay > 0) {
      el.style.setProperty("--reveal-delay", `${delay}ms`);
    }

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            el.classList.add("is-visible");
            if (once) io.unobserve(el);
          } else if (!once) {
            el.classList.remove("is-visible");
          }
        }
      },
      { threshold, rootMargin: "0px 0px -8% 0px" }
    );

    io.observe(el);
    return () => io.disconnect();
  }, [delay, threshold, once]);

  const Tag = as as React.ElementType;
  const variantClass =
    variant === "fade"
      ? "scroll-reveal scroll-reveal--fade"
      : variant === "scale"
      ? "scroll-reveal scroll-reveal--scale"
      : "scroll-reveal";

  return (
    <Tag ref={ref as React.Ref<HTMLElement>} className={`${variantClass} ${className}`}>
      {children}
    </Tag>
  );
}
